/** @jsx jsx */

import React, { Fragment as _, useState, useRef, useContext } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import { EditWrapper0 } from './EditWrappers';
import { useCachedResource } from 'services/useCachedResource';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';
import { State } from 'store/store';
import { interactionContext as InteractionContext } from 'components/lib/TextAndImage/interactionContext';

const OpenPDFImage = ({ src, cardBorderRadius, includeText, description }) => {
  const cachedImgSrc = useCachedResource(CHAMELEON_CACHE_v1, src);

  return cachedImgSrc ? (
    <img
      src={cachedImgSrc}
      alt={description}
      style={{
        width: '100%',
        borderRadius: includeText
          ? `${cardBorderRadius}px ${cardBorderRadius}px 0 0`
          : cardBorderRadius,
      }}
    />
  ) : null;
};

const OpenPDF = ({
  id,
  mediaParentId,
  selected,
  src: imgSrc,
  pdfSrc,
  children,
  cardColor,
  cardBorderRadius,
  cardOutline,
  includeImage,
  description,
  includeText,
  includeShadow,
  focusColor,
}) => {
  const interactionContext = useContext(InteractionContext);

  const layout = interactionContext?.meta?.layout;
  let hasOneColumnLayout;

  if (layout && layout.toLowerCase().includes('one')) {
    hasOneColumnLayout = true;
  }
  //
  const highContrast = useSelector((state: State) => {
    return state.theme.highContrast;
  });

  const [containerHilite, setContainerHilite] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <div
        ref={container}
        css={css({
          position: 'relative',
          // width: '100%',
          // height: '100%',
          fontSize: '14px',
          fontWeight: 500,
          cursor: window.dev && !window.dev() ? 'pointer' : 'auto',
          ...(includeShadow && {
            boxShadow: '0px 0px 11.7411px rgba(13, 16, 40, 0.15)',
          }),

          borderRadius: cardBorderRadius,

          ...(containerHilite && {
            boxShadow: `0 0 0 2px #fff, 0 0 0 ${
              highContrast ? 5 : 3
            }px ${focusColor}`,
            transition: 'box-shadow 0.2s',
          }),

          ...(hasOneColumnLayout && {
            maxWidth: '50vw',
          }),
        })}
      >
        {window.dev() ? (
          <div
            css={css({
              display: 'flex',
              flexDirection: 'column',
            })}
          >
            <EditWrapper0
              id={id}
              mediaParentId={mediaParentId}
              selected={selected}
            >
              <OpenPDFImage
                src={imgSrc}
                cardBorderRadius={cardBorderRadius}
                includeText={includeText}
                description={description}
              />
            </EditWrapper0>
          </div>
        ) : (
          <a
            href={pdfSrc}
            title={'Click to open pdf'}
            target="_blank"
            onFocus={e => {
              setContainerHilite(true);
            }}
            onBlur={() => {
              setContainerHilite(false);
            }}
            onKeyUp={e => {
              if (e.keyCode === 32) {
                e.currentTarget.click();
              }
            }}
            onMouseOver={e => {
              e.currentTarget.focus();
            }}
            onMouseOut={e => {
              e.currentTarget.blur();
            }}
            onClick={e => {
              if (!pdfSrc) {
                alert(
                  'Oops! No file has been specified. Check the source in your pdf settings.'
                );
                e.preventDefault();
                return;
              }
            }}
          >
            <OpenPDFImage
              src={imgSrc}
              cardBorderRadius={cardBorderRadius}
              includeText={includeText}
              description={description}
            />
          </a>
        )}

        {includeText && (
          <div
            css={css({
              position: 'relative',
              textAlign: 'center',
              padding: window.dev() ? '3px 0' : '13px',
              fontWeight: 300,
              //
              background: cardColor,
              borderRadius: `0 0 ${cardBorderRadius}px ${cardBorderRadius}px`,
            })}
            tabIndex={0}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export { OpenPDF };
